import dayjs from "dayjs";

import type * as PortfolioTypes from "@src/types/portfolio";
import { isPositionFinanciallyClosed, isPositionTrackingEnded } from "@src/utils/positionStatus";
import type * as TDT from "@tradingdroid/frontends-types";

import type * as TVL from "lightweight-charts";

// Money put into the execution: starting capital plus deposits, minus withdrawals.
export const getExecutionCapital = (exec: TDT.ApiExecution): number =>
  exec.metrics?.invested_capital ??
  exec.metrics?.initial_capital ??
  exec.config?.portfolio?.initial_capital ??
  0;

/**
 * Generate daily snapshots for a single execution based on its metrics
 * Uses linear interpolation between start and end based on cumulative returns
 */
const generateExecutionDailySnapshots = (
  execution: TDT.ApiExecution,
): PortfolioTypes.StrategyDailySnapshot[] => {
  const snapshots: PortfolioTypes.StrategyDailySnapshot[] = [];

  const startDate = new Date(execution.start_ts);
  const endDate = execution.end_ts ? new Date(execution.end_ts) : new Date();

  const initialCapital = getExecutionCapital(execution);
  const totalPnl = execution.metrics?.cumulative_returns || 0;
  const totalDays = Math.max(
    1,
    Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)),
  );

  // Generate daily snapshots with linear progression
  // In reality, capital growth isn't linear, but without daily parquet data, this is our best approximation
  for (let day = 0; day <= totalDays; day++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + day);
    currentDate.setHours(0, 0, 0, 0);

    // Skip future dates
    if (currentDate > new Date()) break;

    const progress = day / totalDays;
    const currentPnl = totalPnl * progress;
    const currentCapital = initialCapital + currentPnl;

    snapshots.push({
      date: currentDate.toISOString().split("T")[0],
      timestamp: Math.floor(currentDate.getTime() / 1000),
      capital: currentCapital,
      pnl: currentPnl,
      pnlPct: initialCapital > 0 ? (currentPnl / initialCapital) * 100 : 0,
      initialCapital,
    });
  }

  return snapshots;
};

/**
 * Process a single execution into strategy performance data
 */
const processExecution = (
  execution: TDT.ApiExecution,
  strategy?: TDT.ApiStrategy,
): PortfolioTypes.StrategyPerformance => {
  const dailySnapshots = generateExecutionDailySnapshots(execution);

  return {
    executionId: execution.id,
    executionName: execution.name,
    strategyId: execution.strategy_id,
    strategyName:
      strategy?.name || execution.config?.strategy_name || `Strategy ${execution.strategy_id}`,
    symbolName: execution.config?.symbol_name || "Unknown",
    exchangeName: execution.config?.exchange_name || "Unknown",
    initialCapital: getExecutionCapital(execution),
    currentCapital:
      execution.metrics?.ending_capital ??
      execution.metrics?.final_capital ??
      getExecutionCapital(execution) + (execution.metrics?.cumulative_returns || 0),
    totalPnl: execution.metrics?.cumulative_returns || 0,
    totalPnlPct: execution.metrics?.cumulative_returns_pct || 0,
    winRate: execution.metrics?.win_rate || 0,
    totalPositions: execution.metrics?.total_positions || 0,
    maxDrawdownPct: execution.metrics?.max_drawdown_pct || 0,
    sharpeRatio: execution.metrics?.sharpe_ratio || 0,
    startTs: execution.start_ts,
    endTs: execution.end_ts,
    isActive: !execution.end_ts,
    dailySnapshots,
  };
};

/**
 * Aggregate daily snapshots from all strategies into portfolio snapshots
 */
const aggregatePortfolioSnapshots = (
  strategies: PortfolioTypes.StrategyPerformance[],
): PortfolioTypes.PortfolioDailySnapshot[] => {
  // Collect all unique dates across all strategies
  const dateMap = new Map<
    string,
    { totalValue: number; totalPnl: number; totalInitial: number; count: number }
  >();

  strategies.forEach((strategy) => {
    strategy.dailySnapshots.forEach((snapshot) => {
      const existing = dateMap.get(snapshot.date);
      if (existing) {
        existing.totalValue += snapshot.capital;
        existing.totalPnl += snapshot.pnl;
        existing.totalInitial += snapshot.initialCapital;
        existing.count++;
      } else {
        dateMap.set(snapshot.date, {
          totalValue: snapshot.capital,
          totalPnl: snapshot.pnl,
          totalInitial: snapshot.initialCapital,
          count: 1,
        });
      }
    });
  });

  // Convert to array and sort by date
  const snapshots: PortfolioTypes.PortfolioDailySnapshot[] = Array.from(dateMap.entries())
    .map(([date, data]) => ({
      date,
      timestamp: Math.floor(new Date(date).getTime() / 1000),
      totalValue: data.totalValue,
      totalPnl: data.totalPnl,
      totalPnlPct: data.totalInitial > 0 ? (data.totalPnl / data.totalInitial) * 100 : 0,
      strategiesCount: data.count,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);

  return snapshots;
};

/**
 * Calculate aggregate portfolio metrics
 */
const calculatePortfolioMetrics = (
  strategies: PortfolioTypes.StrategyPerformance[],
  portfolioSnapshots: PortfolioTypes.PortfolioDailySnapshot[],
): PortfolioTypes.PortfolioMetrics => {
  if (strategies.length === 0) {
    return {
      totalInitialCapital: 0,
      totalCurrentCapital: 0,
      totalPnl: 0,
      totalPnlPct: 0,
      avgWinRate: 0,
      totalPositions: 0,
      activeStrategiesCount: 0,
      totalStrategiesCount: 0,
      bestPerformingStrategy: null,
      worstPerformingStrategy: null,
      avgSharpeRatio: 0,
      maxPortfolioDrawdownPct: 0,
      portfolioStartDate: new Date().toISOString(),
      portfolioEndDate: new Date().toISOString(),
    };
  }

  const totalInitialCapital = strategies.reduce((sum, s) => sum + s.initialCapital, 0);
  const totalCurrentCapital = strategies.reduce((sum, s) => sum + s.currentCapital, 0);
  const totalPnl = strategies.reduce((sum, s) => sum + s.totalPnl, 0);
  const totalPnlPct = totalInitialCapital > 0 ? (totalPnl / totalInitialCapital) * 100 : 0;

  const avgWinRate = strategies.reduce((sum, s) => sum + s.winRate, 0) / strategies.length;
  const totalPositions = strategies.reduce((sum, s) => sum + s.totalPositions, 0);
  const activeStrategiesCount = strategies.filter((s) => s.isActive).length;

  // Find best and worst performing strategies
  const sortedByPnl = [...strategies].sort((a, b) => b.totalPnlPct - a.totalPnlPct);
  const bestPerformingStrategy =
    sortedByPnl.length > 0
      ? { name: sortedByPnl[0].executionName, pnlPct: sortedByPnl[0].totalPnlPct }
      : null;
  const worstPerformingStrategy =
    sortedByPnl.length > 0
      ? {
          name: sortedByPnl[sortedByPnl.length - 1].executionName,
          pnlPct: sortedByPnl[sortedByPnl.length - 1].totalPnlPct,
        }
      : null;

  const avgSharpeRatio = strategies.reduce((sum, s) => sum + s.sharpeRatio, 0) / strategies.length;

  // Calculate max portfolio drawdown from snapshots
  let peakValue = 0;
  let maxDrawdown = 0;
  portfolioSnapshots.forEach((snapshot) => {
    if (snapshot.totalValue > peakValue) {
      peakValue = snapshot.totalValue;
    }
    if (peakValue > 0) {
      const drawdown = ((peakValue - snapshot.totalValue) / peakValue) * 100;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    }
  });

  // Get date range (filter out invalid dates)
  const allStartDates = strategies
    .map((s) => (s.startTs ? new Date(s.startTs).getTime() : NaN))
    .filter((ts) => !isNaN(ts));
  const allEndDates = strategies
    .map((s) => (s.endTs ? new Date(s.endTs).getTime() : Date.now()))
    .filter((ts) => !isNaN(ts));
  const portfolioStartDate =
    allStartDates.length > 0
      ? new Date(Math.min(...allStartDates)).toISOString()
      : new Date().toISOString();
  const portfolioEndDate =
    allEndDates.length > 0
      ? new Date(Math.max(...allEndDates)).toISOString()
      : new Date().toISOString();

  return {
    totalInitialCapital,
    totalCurrentCapital,
    totalPnl,
    totalPnlPct,
    avgWinRate,
    totalPositions,
    activeStrategiesCount,
    totalStrategiesCount: strategies.length,
    bestPerformingStrategy,
    worstPerformingStrategy,
    avgSharpeRatio,
    maxPortfolioDrawdownPct: maxDrawdown,
    portfolioStartDate,
    portfolioEndDate,
  };
};

/**
 * Filter executions to only include those from the last N months
 */
export const filterExecutionsByTimeRange = (
  executions: TDT.ApiExecution[],
  timeRange: PortfolioTypes.TimeRangeFilter,
): TDT.ApiExecution[] => {
  if (timeRange === "ALL") {
    return executions;
  }

  const now = new Date();
  let cutoffDate: Date;

  switch (timeRange) {
    case "1M":
      cutoffDate = new Date(now.setMonth(now.getMonth() - 1));
      break;
    case "3M":
      cutoffDate = new Date(now.setMonth(now.getMonth() - 3));
      break;
    case "6M":
      cutoffDate = new Date(now.setMonth(now.getMonth() - 6));
      break;
    case "1Y":
    default:
      cutoffDate = new Date(now.setFullYear(now.getFullYear() - 1));
      break;
  }

  return executions.filter((exec) => {
    const startDate = new Date(exec.start_ts);
    const endDate = exec.end_ts ? new Date(exec.end_ts) : new Date();
    // Include if the execution was active during the time range
    return endDate >= cutoffDate || startDate >= cutoffDate;
  });
};

// Forex pairs quoted outside USD store 0 in the USDT field; their profit sits in the quote field.
export const getPositionPnlUsd = (p: TDT.ApiPosition): number | null => {
  if (isPositionTrackingEnded(p)) return null;
  const usdt = p.net_profit_usdt ?? 0;
  if (usdt !== 0) return usdt;
  return p.net_profit_quote ?? 0;
};

// True when the execution was running at any point between `from` and `to` (UTC).
export const isExecutionInRange = (exec: TDT.ApiExecution, from: string, to: string): boolean => {
  const rangeFrom = dayjs.utc(from);
  const rangeTo = dayjs.utc(to);
  if (rangeTo.isValid() && dayjs.utc(exec.start_ts).isAfter(rangeTo)) return false;
  if (exec.end_ts && rangeFrom.isValid() && dayjs.utc(exec.end_ts).isBefore(rangeFrom)) {
    return false;
  }
  return true;
};

/**
 * Portfolio metrics for the trades closed in a date range, on the capital of `executions`.
 */
export const calculateRangeMetrics = (
  positions: TDT.ApiPosition[],
  executions: TDT.ApiExecution[],
  from: string,
  to: string,
): PortfolioTypes.PortfolioMetrics => {
  const capitalByExec = new Map<number, number>();
  for (const exec of executions) capitalByExec.set(exec.id, getExecutionCapital(exec));
  const totalCapital = [...capitalByExec.values()].reduce((sum, c) => sum + c, 0);

  const closedPositions = positions.filter(isPositionFinanciallyClosed);
  const pnlByExec = new Map<number, number>();
  let totalPnl = 0;
  let wins = 0;
  for (const p of closedPositions) {
    const pnl = getPositionPnlUsd(p)!;
    totalPnl += pnl;
    if (pnl > 0) wins += 1;
    if (p.execution_id) pnlByExec.set(p.execution_id, (pnlByExec.get(p.execution_id) ?? 0) + pnl);
  }

  const execResults = executions
    .filter((e) => pnlByExec.has(e.id))
    .map((e) => {
      const capital = capitalByExec.get(e.id) ?? 0;
      const pnl = pnlByExec.get(e.id) ?? 0;
      return { name: e.name, pnlPct: capital > 0 ? (pnl / capital) * 100 : 0 };
    })
    .sort((a, b) => b.pnlPct - a.pnlPct);

  // Money line rebuilt from each closed trade in closing order; drops inside an open trade are not seen.
  const ordered = [...closedPositions].sort(
    (a, b) => dayjs.utc(a.end_ts).valueOf() - dayjs.utc(b.end_ts).valueOf(),
  );
  let value = totalCapital;
  let peak = totalCapital;
  let maxDrawdownPct = 0;
  for (const p of ordered) {
    value += getPositionPnlUsd(p)!;
    if (value > peak) peak = value;
    if (peak > 0) maxDrawdownPct = Math.max(maxDrawdownPct, ((peak - value) / peak) * 100);
  }

  return {
    totalInitialCapital: totalCapital,
    totalCurrentCapital: totalCapital + totalPnl,
    totalPnl,
    totalPnlPct: totalCapital > 0 ? (totalPnl / totalCapital) * 100 : 0,
    avgWinRate: closedPositions.length > 0 ? wins / closedPositions.length : 0,
    totalPositions: closedPositions.length,
    activeStrategiesCount: executions.filter((e) => !e.end_ts).length,
    totalStrategiesCount: executions.length,
    bestPerformingStrategy: execResults[0] ?? null,
    worstPerformingStrategy: execResults[execResults.length - 1] ?? null,
    avgSharpeRatio: null,
    maxPortfolioDrawdownPct: maxDrawdownPct,
    portfolioStartDate: dayjs.utc(from).toISOString(),
    portfolioEndDate: dayjs.utc(to).toISOString(),
  };
};

/**
 * Main function to process executions into portfolio data
 */
export const processPortfolioData = (
  executions: TDT.ApiExecution[],
  strategies: TDT.ApiStrategy[],
): PortfolioTypes.PortfolioData => {
  // Create strategy lookup map
  const strategyMap = new Map<number, TDT.ApiStrategy>();
  strategies.forEach((s) => strategyMap.set(s.id, s));

  // Process each execution
  const strategyPerformances = executions.map((exec) =>
    processExecution(exec, strategyMap.get(exec.strategy_id)),
  );

  // Aggregate portfolio snapshots
  const portfolioSnapshots = aggregatePortfolioSnapshots(strategyPerformances);

  // Calculate metrics
  const metrics = calculatePortfolioMetrics(strategyPerformances, portfolioSnapshots);

  return {
    strategies: strategyPerformances,
    portfolioSnapshots,
    metrics,
  };
};

/**
 * Generate color palette for chart series
 */
export const getStrategyColors = (count: number): string[] => {
  const baseColors = [
    "#2196F3", // Blue
    "#4CAF50", // Green
    "#FF9800", // Orange
    "#9C27B0", // Purple
    "#00BCD4", // Cyan
    "#E91E63", // Pink
    "#8BC34A", // Light Green
    "#FF5722", // Deep Orange
    "#673AB7", // Deep Purple
    "#3F51B5", // Indigo
    "#009688", // Teal
    "#CDDC39", // Lime
    "#FFC107", // Amber
    "#795548", // Brown
    "#607D8B", // Blue Grey
  ];

  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(baseColors[i % baseColors.length]);
  }
  return colors;
};

/**
 * Create TVLight series data for individual strategy PnL
 */
export const createStrategyPnLSeries = (
  strategy: PortfolioTypes.StrategyPerformance,
  color: string,
): PortfolioTypes.PortfolioChartSeries => {
  return {
    type: "Line",
    data: strategy.dailySnapshots.map((snapshot) => ({
      time: snapshot.timestamp as TVL.Time,
      value: snapshot.pnl,
    })),
    options: {
      title: strategy.executionName,
      color,
      lineWidth: 2,
    },
  };
};

/**
 * Create TVLight series for accumulated portfolio value
 */
export const createPortfolioValueSeries = (
  portfolioSnapshots: PortfolioTypes.PortfolioDailySnapshot[],
): PortfolioTypes.PortfolioChartSeries => {
  return {
    type: "Area",
    data: portfolioSnapshots.map((snapshot) => ({
      time: snapshot.timestamp as TVL.Time,
      value: snapshot.totalValue,
    })),
    options: {
      title: "Portfolio Value",
      color: "rgba(33, 150, 243, 0.3)", // Semi-transparent blue
      lineWidth: 3,
    },
  };
};

/**
 * Create TVLight series for accumulated portfolio PnL
 */
export const createPortfolioPnLSeries = (
  portfolioSnapshots: PortfolioTypes.PortfolioDailySnapshot[],
): PortfolioTypes.PortfolioChartSeries => {
  return {
    type: "Baseline",
    data: portfolioSnapshots.map((snapshot) => ({
      time: snapshot.timestamp as TVL.Time,
      value: snapshot.totalPnl,
    })),
    options: {
      title: "Total PnL",
      color: "#4CAF50",
      lineWidth: 3,
    },
  };
};

/**
 * Format currency value
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format percentage value
 */
export const formatPercentage = (value: number): string => {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
};
